import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Banner.css";
import "../styles/global.css";

/* ────────── Outline SVG Icons ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {d}
  </svg>
);

const SearchIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </>
    }
  />
);
const EyeIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    }
  />
);
const TrashIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
      </>
    }
  />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const TagIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </>
    }
  />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const ChevronRightIcon = (p) => (
  <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bn-pagination">
      <div className="bn-pagination__info">
        <span>
          Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="bn-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="bn-pagination__pages">
        <button
          className="bn-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="bn-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`bn-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          className="bn-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Component ────────── */
const ContactEnquiry = () => {
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [filterText, setFilterText] = useState("");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  // Bulk-selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // View-details modal state
  const [viewRow, setViewRow] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Fetch Enquiries ── */
  const fetchEnquiries = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/contact-enquiries`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setEnquiries(data.data);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Error fetching contact enquiries:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  /* ── Search filter (memoised) ── */
  const filteredEnquiries = useMemo(() => {
    if (!filterText) return enquiries;
    const ft = filterText.toLowerCase();
    return enquiries.filter(
      (row) =>
        row.name?.toLowerCase().includes(ft) ||
        row.email?.toLowerCase().includes(ft) ||
        row.phone?.toLowerCase().includes(ft) ||
        row.subject?.toLowerCase().includes(ft) ||
        row.comments?.toLowerCase().includes(ft) ||
        row.id?.toString().toLowerCase().includes(ft)
    );
  }, [filterText, enquiries]);

  /* ── Reset to page 1 when filter changes ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  /* ── Clamp currentPage if out of range (e.g. after delete) ── */
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredEnquiries.length / perPage));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredEnquiries.length, perPage, currentPage]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessageAlert("");
      setMessageType("");
    }, 4000);
  };

  /* ── Delete single ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Enquiry",
      message: "Are you sure you want to delete this enquiry? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/contact-enquiries/destroy/${id}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Enquiry deleted successfully!", "success");
              fetchEnquiries();
            } else {
              flashMessage("Failed to delete enquiry.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting enquiry.", "danger"));
      },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;

    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected enquir${count > 1 ? "ies" : "y"}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/contact-enquiries/destroy/${row.id}`, {
              method: "GET",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }).then((res) => res.json())
          )
        )
          .then(() => {
            flashMessage(
              `${count} enquir${count > 1 ? "ies" : "y"} deleted successfully!`,
              "success"
            );
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            fetchEnquiries();
          })
          .catch(() => flashMessage("Error deleting enquiries!", "danger"));
      },
    });
  };

  /* ── Pagination helpers ── */
  const totalRows = filteredEnquiries.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredEnquiries.slice(start, start + perPage);
  }, [filteredEnquiries, currentPage, perPage]);

  const truncate = (str, len = 60) => {
    if (!str) return "-";
    return str.length > len ? str.slice(0, len) + "…" : str;
  };

  /* ────────── Table Columns ────────── */
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row) => <span className="id-pill">#{row.id}</span>,
        width: "90px",
        sortable: true,
        selector: (row) => row.id,
      },
      {
        name: "Name",
        selector: (row) => row.name,
        cell: (row) => <span className="cell-name">{row.name}</span>,
        sortable: true,
      },
      {
        name: "Email",
        selector: (row) => row.email,
        cell: (row) => <span className="cell-muted">{row.email}</span>,
        sortable: true,
      },
      {
        name: "Phone",
        selector: (row) => row.phone,
        cell: (row) => <span className="cell-muted">{row.phone || "-"}</span>,
      },
      {
        name: "Subject",
        selector: (row) => row.subject,
        cell: (row) => <span className="type-pill">{row.subject || "General"}</span>,
      },
      {
        name: "Message",
        cell: (row) => <span className="cell-muted">{truncate(row.comments)}</span>,
        grow: 2,
      },
      {
        name: "Mail Status",
        cell: (row) => (
          <span
            className={`status-pill ${row.mail_status === "Sent" ? "is-active" : "is-inactive"}`}
          >
            <span className="dot" />
            {row.mail_status || "Pending"}
          </span>
        ),
      },
      {
        name: "Received",
        selector: (row) => row.created_at,
        cell: (row) => (
          <span className="cell-muted">
            {row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : "-"}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="row-actions">
            <button
              className="ico-btn edit"
              onClick={() => setViewRow(row)}
              title="View Enquiry"
            >
              <EyeIcon size={16} />
            </button>
            <button
              className="ico-btn del"
              onClick={() => handleDelete(row.id)}
              title="Delete Enquiry"
            >
              <TrashIcon size={16} />
            </button>
          </div>
        ),
        width: "120px",
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
        "&:hover": { background: "#f6fcf9" },
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: {
      style: { paddingLeft: "16px", paddingRight: "16px" },
    },
    pagination: { style: { display: "none" } },
  };

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {loading && (
          <div className="bn-overlay">
            <div className="bn-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="banner-shell">

              {/* ── Page header ── */}
              <div className="banner-head">
                <div>
                  <h2>Contact Enquiries</h2>
                  <p>Messages submitted through the website's Contact Us form.</p>
                </div>
              </div>

              {/* ── Card ── */}
              <div className="banner-card">
                <div className="card-top">
                  <div className="search-box">
                    <SearchIcon size={18} />
                    <input
                      type="text"
                      placeholder="Search by name, email, phone, subject…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="total-badge">
                    <TagIcon size={16} />
                    Total:&nbsp;<b>{filteredEnquiries.length}</b>
                  </div>
                </div>

                {messagealert && (
                  <div className={`bn-alert ${messageType}`} role="alert">
                    <span>{messagealert}</span>
                    <button
                      type="button"
                      onClick={() => setMessageAlert("")}
                      aria-label="Close"
                    >
                      <CloseIcon size={16} />
                    </button>
                  </div>
                )}

                {selectedRows.length > 0 && (
                  <div className="bulk-bar">
                    <div className="count">
                      <span className="badge-num">{selectedRows.length}</span>
                      item{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="actions">
                      <button
                        className="bulk-btn ghost"
                        onClick={() => {
                          setSelectedRows([]);
                          setToggleCleared(!toggleCleared);
                        }}
                      >
                        Clear
                      </button>
                      <button
                        className="bulk-btn danger"
                        onClick={handleBulkDelete}
                      >
                        <TrashIcon size={14} />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) =>
                    setSelectedRows(selectedRows)
                  }
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div
                      style={{
                        padding: "40px 20px",
                        color: "var(--bn-muted)",
                        textAlign: "center",
                      }}
                    >
                      No contact enquiries found.
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {filteredEnquiries.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── View Details Modal (plain, no Bootstrap JS dependency) ────────── */}
          {viewRow && (
            <div
              className="cdlg-overlay"
              role="dialog"
              aria-modal="true"
              onClick={() => setViewRow(null)}
            >
              <div
                className="cdlg-box"
                style={{ textAlign: "left", maxWidth: "480px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h3 className="cdlg-title" style={{ margin: 0 }}>Enquiry #{viewRow.id}</h3>
                  <button
                    type="button"
                    className="ico-btn"
                    onClick={() => setViewRow(null)}
                    aria-label="Close"
                  >
                    <CloseIcon size={18} />
                  </button>
                </div>

                <div style={{ display: "grid", rowGap: "10px", fontSize: "14px" }}>
                  <div><b>Name:</b> {viewRow.name}</div>
                  <div><b>Email:</b> {viewRow.email}</div>
                  <div><b>Phone:</b> {viewRow.phone || "-"}</div>
                  <div><b>Subject:</b> {viewRow.subject || "-"}</div>
                  <div>
                    <b>Message:</b>
                    <p style={{ marginTop: "4px", whiteSpace: "pre-wrap" }}>{viewRow.comments}</p>
                  </div>
                  <div><b>Mail Status:</b> {viewRow.mail_status || "Pending"}</div>
                  <div>
                    <b>Received:</b>{" "}
                    {viewRow.created_at ? new Date(viewRow.created_at).toLocaleString("en-IN") : "-"}
                  </div>
                </div>

                <div className="cdlg-actions" style={{ marginTop: "20px" }}>
                  <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={() => setViewRow(null)}>
                    Close
                  </button>
                  <a
                    href={`mailto:${viewRow.email}`}
                    className="cdlg-btn cdlg-btn-confirm cdlg-btn-warning"
                    style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  >
                    Reply by Email
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ────────── Custom Confirm Dialog (replaces window.confirm) ────────── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default ContactEnquiry;